import React, { useContext, useState } from "react";
import logo from "../assets/01pos.webp";
import { axiosInstance } from "../axiosInstance";
import { Link, useNavigate } from "react-router";
import { notify } from "../components/toastify";
import loader from "../assets/loader.webp";
import useRotatingImages from "../utility/useRotatingImages";
import { ProfileDetailsContext } from "../context/ProfileDetailsProvider";
import CalculateIcon from "@mui/icons-material/Calculate";
import Calculator from "../utility/Calculator";
import { rememberLogin, verifyOfflineLogin } from "../utility/offline/offlineLogin";

function Login({ setIsAuthenticated, setUserRole }) {
  const { setProfileDetails } = useContext(ProfileDetailsContext);
  const currentImage = useRotatingImages(10000);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [login_pin, setLogin_pin] = useState("");
  const [passform, setPassform] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculator, setCalculator] = useState(false);
  const navigate = useNavigate();

  // Function to handle API POST request for login
  const postData = async (newData) => {
    setLoading(true); // Set loading to true before the API call
    try {
      const response = await axiosInstance.post("api/auth/login", newData);
      const responseData = response?.data;
      console.log(responseData, "response data form login response")

      localStorage.setItem("myToken", responseData?.data?.accessToken )
console.log(responseData?.data?.accessToken,)
      // Check if this is an expired license error (error: true but has user data and accessToken)
      // This is a special case where backend returns error: true but still provides user data and token
      const isExpiredLicenseError =
        responseData.error === true &&
        responseData.data?.user &&
        responseData.data?.accessToken &&
        responseData.data?.subscription;

      // Handle both success and expired license error cases
      if (responseData.error === false || isExpiredLicenseError) {
        // Store profile details
        setProfileDetails(responseData.data);

        const name = responseData.data?.user?.roles?.[0]?.name;
        const { accessToken, subscription } = responseData.data;

        if (!name || !accessToken) {
          notify("Invalid response from server", "error");
          return;
        }

        // Store token
        sessionStorage.setItem(
          "token",
          JSON.stringify({ accessToken, role: name })
        );

        setUserRole(name);

        // Store role-specific token
        if (name === "Super Admin") {
          sessionStorage.setItem("superadmin_token", accessToken);
        } else if (name === "Admin") {
          sessionStorage.setItem("admin_token", accessToken);
        } else if (name === "Manager") {
          sessionStorage.setItem("manager_token", accessToken);
        } else if (name === "Cashier") {
          sessionStorage.setItem("cashier_token", accessToken);
        } else if (name === "Supervisor") {
          sessionStorage.setItem("supervisor_token", accessToken);
        }

        // Check for expired subscription
        const isExpired = subscription?.is_plan_expired || isExpiredLicenseError;
        console.log(responseData)

        if (isExpired) {
          // Show expired license message from backend
          notify(
            responseData.message || "Your subscription has expired. Please renew to continue.",
            "error"
          );

          navigate("/renew");
          setIsAuthenticated(true);
          return;
        } else {
          if (name === "Cashier") rememberLogin(newData, responseData.data);
          // Normal success case - show success message
          notify(responseData.message || "Login successful", "success");
        }

        // Navigate based on role (skip navigation for expired Admin as they're redirected above)
        if (!(isExpired && name === "Admin")) {
          if (name === "Super Admin") {
            navigate("/superadmin");
            setIsAuthenticated(true);
          } else if (name === "Admin") {
            navigate("/admin");
            setIsAuthenticated(true);
          } else if (name === "Manager") {
            navigate("/manager");
            setIsAuthenticated(true);
          } else if (name === "Cashier") {
            navigate("/cashier");
            setIsAuthenticated(true);
          } else if (name === "Supervisor") {
            navigate("/supervisordashboard");
            setIsAuthenticated(true);
          }
        }
      } else if (responseData.error === true) {
        // Normal error case (no user data, no accessToken) - invalid credentials, etc.
        notify(responseData.message || "Login failed", "error");
      }
    } catch (err) {
      if (!err?.response) {
        // No connection: let a cashier who has logged in here before back in.
        const saved = await verifyOfflineLogin(newData);
        if (saved?.accessToken && saved?.user?.roles?.[0]?.name === "Cashier") {
          setProfileDetails(saved);
          localStorage.setItem("myToken", saved.accessToken);
          sessionStorage.setItem(
            "token",
            JSON.stringify({ accessToken: saved.accessToken, role: "Cashier" })
          );
          sessionStorage.setItem("cashier_token", saved.accessToken);
          setUserRole("Cashier");
          notify("You're offline. Logged in with your saved login.", "success");
          navigate("/cashier");
          setIsAuthenticated(true);
          return;
        }
        notify(
          "No internet connection. Log in once online on this device to enable offline login.",
          "error"
        );
        return;
      }
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const newData = passform ? { login_pin } : { email, password };
    postData(newData);
  };

  const handlePassform = (state) => {
    setPassform(state);
  };

  return (
    <div
      className="relative w-full h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${currentImage})` }}
    >
      {calculator && <Calculator handleClose={setCalculator} />}
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black opacity-20 z-10"></div>
      <div className="flex justify-center relative items-center px-4 h-screen z-20">
        <div className="flex flex-col gap-6 px-6 py-8 w-full rounded-md mx-4 md:max-w-[550px] bg-white">
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <div className="flex justify-between items-center">
              <div>
                <img src={logo} alt="logo" className="w-24" />
                <h2 className="text-3xl font-semibold">Welcome to 01 POS</h2>
              </div>
              <CalculateIcon
                sx={{ fontSize: "50px" }}
                onClick={() => setCalculator(true)}
                className="cursor-pointer"
              />
            </div>

            {!passform && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col">
                  <label htmlFor="email">Email</label>
                  <input
                    className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                    placeholder="Youremail@mail.com"
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label htmlFor="password">Password</label>
                  <input
                    className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                    placeholder="Password"
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <Link to="/resetpassword">
                    <button
                      type="button"
                      className="text-sm text-gray-950 hover:text-gray-800"
                    >
                      Forgot password?
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {passform && (
              <div className="flex flex-col">
                <label htmlFor="passcode">Passcode</label>
                <input
                  className="border-2 border-gray-300 p-2 rounded-md focus:border-teal-500 focus:outline-none"
                  placeholder="Enter Passcode"
                  type="password"
                  id="passcode"
                  value={login_pin}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                    setLogin_pin(numericValue);
                  }}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              className="text-lg font-semibold p-2 text-white rounded-lg bg-[#2f345d] w-full flex justify-center hover:bg-[#455093] transition-all"
            >
              {loading ? (
                <img src={loader} alt="lodr" className="w-8" />
              ) : (
                "Login"
              )}
            </button>
          </form>

          {!passform ? (
            <button
              onClick={() => handlePassform(true)}
              className="text-lg font-semibold p-2 rounded-lg border-red-500 border-2 flex items-center justify-center text-center text-red-500 transition-all cursor-pointer hover:bg-red-500 hover:text-white"
            >
              Login with Passcode instead
            </button>
          ) : (
            <button
              onClick={() => handlePassform(false)}
              className="text-lg font-semibold p-2 rounded-lg border-red-500 border-2 flex items-center justify-center text-center text-red-500 transition-all cursor-pointer hover:bg-red-500 hover:text-white"
            >
              Login with Email
            </button>
          )}

          {/* Section to route to sign up page */}
          <div className="text-center">
            <p className="text-gray-600">
              Don't have an account?{" "}
              <Link to="/signup" className="text-blue-600 hover:underline font-semibold">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
